import { useState } from 'react';
import styled, { css } from 'styled-components';
import { Field, Form, useFormikContext } from 'formik';
import { Trans } from 'react-i18next';

import { IGiftCardDetailsForm } from 'gift-cards/types';
import { Button } from 'shared/components/Button';
import { Choice } from 'shared/components/Choice';
import { FormikInput } from 'shared/components/FormikInput';
import { Link } from 'shared/components/Link';
import { useWindowSize } from 'shared/hooks/useWindowSize';
import { variableFontRegular } from 'styles/theme/text';
import { t } from 'localization/utils/t';
import { Input } from 'shared/components/Input';

const TP = 'giftCards.components.pdp.GiftCardDetailsModal';

interface IGiftCardDetailsModalProps {
  darkTheme?: boolean;
  giftCardAmounts: number[];
}

export const GiftCardDetailsModal = ({
  darkTheme = false,
  giftCardAmounts,
}: IGiftCardDetailsModalProps) => {
  const [characterCount, setCharacterCount] = useState(0);
  const { isMobile } = useWindowSize();
  const { dirty, errors, isSubmitting, isValid, touched, values, setValues } =
    useFormikContext<IGiftCardDetailsForm>();
  const { windowSize } = useWindowSize();

  const maxHeight = 0.57 * windowSize.height < 750 ? 0.57 * windowSize.height : 750;
  const modalHeight = maxHeight < 256 ? 256 : maxHeight;

  const formatAmountString = (amount: number): string => {
    if (!amount) return '$';
    const amountString = amount.toString();
    if (amountString.length > 3) {
      return `$${amountString.slice(0, 1)},${amountString.slice(1)}`;
    }
    return `$${amountString}`;
  };

  const sanitizeAndParseAmount = (amount: string): number => {
    return Number.parseInt(amount.replaceAll(/([$,])/g, ''), 10) || null;
  };

  const handleAmountChange = (amount: number | string, isCustomAmount = false) => {
    if (isCustomAmount) {
      if ((amount as string).length > 5) {
        return;
      }
      void setValues({
        ...values,
        customAmount: sanitizeAndParseAmount(amount as string),
        amount: 0,
      });
    } else {
      void setValues({
        ...values,
        amount: amount as number,
        customAmount: 0,
      });
    }
  };

  const handleMessageChange = (event) => {
    const typedCharacters = event.target?.value?.length || 0;
    setCharacterCount(typedCharacters);
    void setValues({
      ...values,
      message: event.target?.value,
    });
  };

  return (
    <GiftCardDetailsModal.Wrapper>
      <Form>
        <GiftCardDetailsModal.FormWrapper data-qa="gift_card_details_modal">
          <GiftCardDetailsModal.Modal
            $darkTheme={darkTheme}
            $height={modalHeight}
            $isMobile={isMobile}
          >
            <GiftCardDetailsModal.Margin>
              <GiftCardDetailsModal.Label data-qa="gift_card_amount_label">
                {t(`${TP}.selectAmount`, 'Select Amount')}
              </GiftCardDetailsModal.Label>
              <GiftCardDetailsModal.AmountWrapper data-qa="gift_card_amount_wrapper">
                {giftCardAmounts.map((amount: number) => (
                  <GiftCardDetailsModal.AmountSelection
                    data-qa="gift_card_amount_selection"
                    name="expectedPriceCents"
                    key={amount}
                    checked={values.amount === amount}
                    label={`$${amount}`}
                    type="radio"
                    value={amount}
                    onChange={() => handleAmountChange(amount)}
                    $darkTheme={darkTheme}
                  />
                ))}
                {touched?.amount && errors?.amount && (
                  <GiftCardDetailsModal.Error data-qa="gift_card_details_error">
                    {errors?.amount}
                  </GiftCardDetailsModal.Error>
                )}
              </GiftCardDetailsModal.AmountWrapper>
              <GiftCardDetailsModal.CustomAmountInputWrapper>
                <GiftCardDetailsModal.CustomAmountLabel data-qa="gift_card_custom_amount_label">
                  {t(`${TP}.customAmount`, 'Custom Amount')}
                </GiftCardDetailsModal.CustomAmountLabel>
                <GiftCardDetailsModal.CustomAmountSubLabel data-qa="gift_card_custom_amount_sub_label">
                  {t(`${TP}.amountRange`, 'Amount must be between $25 - $2,000.')}
                </GiftCardDetailsModal.CustomAmountSubLabel>
                <GiftCardDetailsModal.CustomAmountInput
                  data-qa="gift_card_custom_amount_input"
                  name="customAmount"
                  onChange={(e) => handleAmountChange(e.target.value, true)}
                  value={formatAmountString(values.customAmount)}
                  errorMessage={errors?.customAmount}
                  hasError={!!errors?.customAmount}
                  inputMode="numeric"
                  type={isMobile ? 'tel' : 'text'}
                />
              </GiftCardDetailsModal.CustomAmountInputWrapper>
              <GiftCardDetailsModal.FormikInput
                label={t(`${TP}.to`, 'To')}
                name="recipientName"
                data-qa="gift_card_recipient_name_input"
              />
              <GiftCardDetailsModal.FormikInput
                label={t(`${TP}.from`, 'From')}
                name="senderName"
                data-qa="gift_card_sender_name_input"
              />
              <GiftCardDetailsModal.FormikInput
                label={t(`${TP}.recipientEmail`, 'Recipient Email')}
                name="recipientEmail"
                data-qa="gift_card_recipient_email_input"
              />
              <GiftCardDetailsModal.Message>
                <GiftCardDetailsModal.Textarea
                  data-qa="gift_card_message_input"
                  id="message"
                  component="textarea"
                  name="message"
                  label={t(`${TP}.message`, 'Message')}
                  placeholder={t(`${TP}.message`, 'Message (Optional)')}
                  maxLength={100}
                  onChange={handleMessageChange}
                />
                <GiftCardDetailsModal.CharacterCount>
                  <span>{characterCount}</span>
                  <span>/100</span>
                </GiftCardDetailsModal.CharacterCount>
              </GiftCardDetailsModal.Message>
              {errors?.message && (
                <GiftCardDetailsModal.Error>{errors?.message}</GiftCardDetailsModal.Error>
              )}
            </GiftCardDetailsModal.Margin>
          </GiftCardDetailsModal.Modal>
          {isMobile && (
            <>
              <GiftCardDetailsModal.TermsAndConditions data-qa="gift_card_tac">
                <Trans
                  i18nKey={`${TP}.terms`}
                  defaults="<0>By proceeding, you agree to the</0> <1>Gift Card Terms</1>"
                  components={[
                    <span />,
                    <Link
                      href="/gift-card-terms"
                      passHref
                      prefetch={false}
                      target="_blank"
                      style={{ textDecoration: 'underline' }}
                    />,
                  ]}
                />
              </GiftCardDetailsModal.TermsAndConditions>
              <GiftCardDetailsModal.Button
                data-qa="gift-cards-pdp-continue"
                title={t(`${TP}.continueToCheckout`, 'Continue to Checkout')}
                inverted={!(isValid && dirty)}
                disabled={isSubmitting || !dirty}
                loading={isSubmitting}
                type="submit"
              />
            </>
          )}
        </GiftCardDetailsModal.FormWrapper>
      </Form>
    </GiftCardDetailsModal.Wrapper>
  );
};

GiftCardDetailsModal.Wrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    margin-bottom: 32px;
  }
`;

GiftCardDetailsModal.FormWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  @media (max-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    width: 100vw;
  }
`;

GiftCardDetailsModal.Modal = styled.div<{
  $darkTheme: boolean;
  $height: number;
  $isMobile: boolean;
}>`
  width: 100%;
  max-height: 506px;
  min-width: 312px;
  padding-bottom: 16px;

  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */
  ::-webkit-scrollbar {
    display: none;
  }

  ${({ $isMobile }) => `
    ${$isMobile ? 'overflow-y: hidden' : 'overflow-y: scroll'};
  `}

  ${({ theme, $darkTheme }) =>
    $darkTheme
      ? css`
          background-color: ${theme.colors.black};
          color: ${theme.colors.white};
          border: ${theme.borders.white};
        `
      : css`
          background-color: ${theme.navigation.backgroundColor};
          color: ${theme.navigation.textColor};
          border: ${theme.navigation.border};
        `};

  @media (max-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    border: none;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    ${({ $height }) => `
      height: ${$height}px;
      width: calc(${$height}px * 0.667);
  `}
  }

  @media (max-height: ${({ theme }) => theme.breakpoints.small}px) {
    ${({ $height, $isMobile }) => `
      height: ${!$isMobile && $height - 100}px;
  `}
  }

  @media (max-height: ${({ theme }) => theme.breakpoints.xSmall}px) {
    ${({ $height, $isMobile }) => `
      height: ${!$isMobile && $height - 175}px;
  `}
  }
`;

GiftCardDetailsModal.Margin = styled.div`
  padding: 16px 16px 16px 16px;
  height: 100%;
  display: flex;
  flex-direction: column;

  @media (max-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    padding: 24px 16px 16px 16px;
  }
`;

GiftCardDetailsModal.Label = styled.label`
  margin-bottom: 16px;
  ${variableFontRegular({ size: '11.5px', height: '14px' })};

  @media (max-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    ${variableFontRegular({ size: '11px', height: '14px' })};
  }
`;

GiftCardDetailsModal.AmountWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr 1fr 1fr;
  gap: 7px;
  margin-bottom: 32px;
`;

GiftCardDetailsModal.CustomAmountInputWrapper = styled.div``;

GiftCardDetailsModal.CustomAmountLabel = styled.p`
  font-size: 11.5px;
  height: 14px;
  margin-bottom: 6px;
`;

GiftCardDetailsModal.CustomAmountSubLabel = styled.p`
  font-size: 9.5px;
  height: 11px;
  margin-bottom: 24px;
`;

GiftCardDetailsModal.CustomAmountInput = styled(Input)`
  width: 150px;
  margin-bottom: 48px;
`;

GiftCardDetailsModal.AmountSelection = styled(Choice)<{ $darkTheme: boolean; checked: boolean }>`
  width: auto;
  min-height: 19px !important;
  padding-top: 4px;
  padding-bottom: 2px;
  ${variableFontRegular({ size: '11.5px', height: '11px' })};

  @media (max-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    ${variableFontRegular({ size: '11px', height: '11px' })};
  }

  border: ${({ theme, $darkTheme }) =>
    $darkTheme ? theme.borders.white : theme.navigation.border} !important;

  ${({ checked, $darkTheme }) =>
    checked &&
    css`
      background-color: ${({ theme }) => ($darkTheme ? theme.colors.white : theme.colors.black)};
      color: ${({ theme }) => ($darkTheme ? theme.colors.black : theme.colors.white)};
    `}

  &:hover {
    ${({ $darkTheme }) => css`
      background-color: ${({ theme }) => ($darkTheme ? theme.colors.white : theme.colors.black)};
      color: ${({ theme }) => ($darkTheme ? theme.colors.black : theme.colors.white)};
    `}
  }
`;

GiftCardDetailsModal.FormikInput = styled(FormikInput)`
  margin-bottom: 24px;
  height: 36px;
`;

GiftCardDetailsModal.Message = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    flex-grow: 1;
  }
`;

GiftCardDetailsModal.Textarea = styled(Field)`
  width: 100%;
  height: 62px;
  border-color: #000;
  outline-color: #000;
  resize: none;
  margin-bottom: 16px;
  ${variableFontRegular({ size: '11.5px', height: '14px' })};
  padding: 12px;
  -webkit-appearance: none;
  border-radius: 0;

  &:focus {
    border-radius: 0px;
  }

  &::placeholder {
    color: #000;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    margin-top: 21px;
    flex-grow: 1;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    ${variableFontRegular({ size: '11px', height: '14px' })};
  }
`;

GiftCardDetailsModal.CharacterCount = styled.div`
  padding-right: 8px;
  padding-bottom: 8px;
  position: absolute;
  bottom: 15px;
  right: 0;
  ${variableFontRegular({ size: '9.5px', height: '9.5px' })};

  @media (max-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    ${variableFontRegular({ size: '9px', height: '9px' })};
  }
`;

GiftCardDetailsModal.Button = styled(Button)`
  padding: 10px;
  height: 32px;
  width: calc(100% - 32px);
  z-index: ${({ theme }) => theme.zIndex.actionButton};
  text-transform: none;

  > span {
    ${variableFontRegular({ size: '11px', height: '12px' })};
  }
`;

GiftCardDetailsModal.Error = styled.div`
  color: ${({ theme }) => theme.colors.red};
  margin: 12px 0;
  ${variableFontRegular({ size: '9.5px', height: '12px' })};

  @media (max-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    ${variableFontRegular({ size: '9px', height: '12px' })};
  }
`;

GiftCardDetailsModal.TermsAndConditions = styled.div`
  text-align: center;
  margin-bottom: 16px;
  ${variableFontRegular({ size: '9px', height: '9px' })};
`;
